import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Receipt, LogOut, Building2, MapPin, ChevronDown, Check, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/apiFetch';

/**
 * Kısıtlı marka rollerinin (FINANCE / GBP) gördüğü tek maddelik sidebar.
 *
 * Kısıt MARKA BAZLIDIR: seçili markanın `currentUserRole` değerine bakılır.
 * Aynı kullanıcı başka bir markada tam panel görebilir — bu yüzden tanım
 * kullanıcıdan değil markadan okunur.
 *
 * Ana Sidebar yerine BİLEREK ayrı bir bileşen: ana sidebar açılırken bağlantı
 * durumu / marka listesi gibi uçlara istek atıyor, kısıtlı rol bunların
 * hepsinden 403 alır (kısıt `middleware/auth.js` → utils/hesapKisitlari.js).
 *
 * TEK istek `GET /api/brands` — kısıtlı tiplerin izin listesinde bilerek açık
 * (hesapKisitlari.js → `getler`). Marka değiştirici buradan besleniyor: kısıtlı
 * kullanıcı 4 markaya erişse bile ana sidebar'ı hiç görmediği için başka
 * geçiş yolu yok, /brand-selector de marka seçiliyken geri yönlendiriyor
 * (ProtectedRoute). Bu liste olmadan ilk seçtiği markaya kilitleniyordu.
 *
 * Yeni bir tip eklerken KISITLI_TIPLER'e bir satır yaz; App.js ve
 * ProtectedRoute buradan okur.
 */
export const KISITLI_TIPLER = {
  finance: { yol: '/faturalama', etiket: 'Faturalama', Icon: Receipt },
  gbp: {
    yol: '/gbp/locations', etiket: 'Lokasyonlar', Icon: MapPin,
    // Markaya özel ek sayfalar. Statik rapor, API çağırmaz — backend kısıtına takılmaz.
    ekler: [
      { yol: '/r/europcar-google-isim', etiket: 'Google Kayıt Adları', Icon: FileText, markalar: ['13b58e06-8266-4a5b-8113-2de43aa686e1'] },
      { yol: '/r/europcar-google-puan', etiket: 'Google Ortalama Puanı', Icon: FileText, markalar: ['13b58e06-8266-4a5b-8113-2de43aa686e1'] },
    ],
  },
};

/** Kısıtlı tipin seçili markada açabildiği sayfalar: ana sayfa + markaya özel ekler. */
export const kisitliSayfalar = (tip, brand) => (tip ? [
  { yol: tip.yol, etiket: tip.etiket, Icon: tip.Icon },
  ...(tip.ekler || []).filter((e) => e.markalar.includes(brand?.id || brand?.brandId)),
] : []);

/** Seçili markadaki kısıt tanımı — tam yetkili markada null. */
export const kisitliTip = (brand) => KISITLI_TIPLER[String(brand?.currentUserRole || '').toLowerCase()] || null;

/** Marka seçildikten sonra açılacak sayfa — BrandSelector'daki kuralın aynısı. */
export const markaAcilisYolu = (brand) => {
  const kisit = kisitliTip(brand);
  if (kisit) return kisit.yol;
  return brand?.currentUserRole === 'BRANDING' ? '/market-insights/facebook-ads-library' : '/overview';
};

export default function RestrictedSidebar({ onHoverChange }) {
  const [isHovered, setIsHovered] = useState(false);
  const [brands, setBrands] = useState([]);
  const [listeAcik, setListeAcik] = useState(false);
  const { logout, selectedBrand, setSelectedBrand, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tip = kisitliTip(selectedBrand);

  // Marka listesi: kısıtlı rolün erişebildiği tek uç. Sessiz başarısızlık —
  // liste gelmezse sidebar eski haliyle (tek marka) çalışmaya devam eder.
  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const r = await apiFetch('https://api.glowytics.com/api/brands');
        if (!r.ok) return;
        const d = await r.json();
        if (!iptal) setBrands(Array.isArray(d.brands) ? d.brands : []);
      } catch (e) {
        // Yut: marka değiştirici bir kolaylıktır, sayfanın kendisi çalışıyor.
      }
    })();
    return () => { iptal = true; };
  }, [user?.id]);

  const hover = (v) => {
    setIsHovered(v);
    if (!v) setListeAcik(false);
    if (onHoverChange) onHoverChange(v);
  };

  const markaSec = useCallback((brand) => {
    setListeAcik(false);
    if (!brand?.id || brand.id === selectedBrand?.id) return;
    setSelectedBrand(brand);
    localStorage.setItem('selectedBrand', JSON.stringify(brand));
    localStorage.setItem('currentBrandId', brand.id);
    navigate(markaAcilisYolu(brand));
  }, [navigate, selectedBrand?.id, setSelectedBrand]);

  if (!tip) return null;
  const cokMarka = brands.length > 1;

  return (
    <div
      className={`fixed top-0 left-0 z-40 flex h-screen flex-col border-r border-gray-200 bg-white
        transition-[width] duration-200 ease-in-out ${isHovered ? 'w-64' : 'w-16'}`}
      onMouseEnter={() => hover(true)}
      onMouseLeave={() => hover(false)}
    >
      <div className={`flex h-16 flex-shrink-0 items-center border-b border-gray-100 px-4 ${isHovered ? 'justify-start' : 'justify-center'}`}>
        <img
          src="/Calisma-Yuzeyi-20@5x-4.png"
          alt="Glowytics"
          className={`h-7 ${isHovered ? 'opacity-100' : 'pointer-events-none absolute opacity-0'}`}
        />
        <img
          src="/logoicon.png"
          alt="Glowytics"
          className={`h-8 w-8 ${isHovered ? 'pointer-events-none absolute opacity-0' : 'opacity-100'}`}
        />
      </div>

      {selectedBrand && (
        <div className="border-b border-gray-100">
          {isHovered ? (
            <button
              type="button"
              onClick={() => cokMarka && setListeAcik((v) => !v)}
              disabled={!cokMarka}
              title={cokMarka ? 'Marka değiştir' : selectedBrand.name}
              className={`flex w-full items-center gap-2 px-4 py-3 text-left transition-colors
                ${cokMarka ? 'hover:bg-gray-50' : 'cursor-default'}`}
            >
              <Building2 size={16} className="shrink-0 text-gray-400" />
              <span className="flex-1 truncate text-[13px] font-semibold text-gray-800">{selectedBrand.name}</span>
              {cokMarka && (
                <ChevronDown
                  size={16}
                  className={`shrink-0 text-gray-400 transition-transform ${listeAcik ? 'rotate-180' : ''}`}
                />
              )}
            </button>
          ) : (
            <div className="flex items-center justify-center py-3" title={selectedBrand.name}>
              <Building2 size={16} className="shrink-0 text-gray-400" />
            </div>
          )}

          {isHovered && listeAcik && cokMarka && (
            <div className="max-h-64 overflow-y-auto border-t border-gray-100 bg-gray-50/60 py-1">
              {brands.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => markaSec(b)}
                  className={`flex w-full items-center gap-2 px-4 py-2 text-left text-[13px] transition-colors
                    ${b.id === selectedBrand.id ? 'font-semibold text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <span className="flex-1 truncate">{b.name}</span>
                  {b.id === selectedBrand.id && <Check size={14} className="shrink-0 text-indigo-600" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <nav className="flex-1 space-y-1 px-2 py-3">
        {kisitliSayfalar(tip, selectedBrand).map(({ yol, etiket, Icon }) => (
          <Link
            key={yol}
            to={yol}
            title={etiket}
            className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors
              ${location.pathname === yol ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}
              ${isHovered ? 'justify-start' : 'justify-center'}`}
          >
            <Icon size={20} className="shrink-0" />
            {isHovered && <span className="text-[14px] font-medium">{etiket}</span>}
          </Link>
        ))}
      </nav>

      <div className="border-t border-gray-100 p-2">
        {isHovered && user?.email && (
          <p className="truncate px-3 pb-2 text-[11px] text-gray-400">{user.email}</p>
        )}
        <button
          onClick={() => logout()}
          title="Çıkış"
          className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-gray-500 transition-colors hover:bg-gray-50
            ${isHovered ? 'justify-start' : 'justify-center'}`}
        >
          <LogOut size={20} className="shrink-0" />
          {isHovered && <span className="text-[14px]">Çıkış</span>}
        </button>
      </div>
    </div>
  );
}
